import type { ReactNode } from "react";

/**
 * The display-type headline shared by every deal-room portal — client, lender,
 * attorney, co-agent, vendor. One h1 treatment so a restyle of one portal
 * can't quietly drift from the others again (see the "Recent activity"
 * ticket that shipped this file: the external portals had grown their own
 * smaller dashboard-font header while the client portal kept the editorial
 * one).
 *
 * `pill` and `meta` are optional because the client's own view has neither —
 * a client already knows who they are and doesn't need "Client: <own name>"
 * printed back at them. External viewers need both: a role pill (they may
 * hold more than one role across deals) and a meta line naming whose deal
 * this is.
 */
interface PortalAddressBlockProps {
  address: string;
  /** The overline row's leading badge — e.g. an ATTORNEY / LENDER role pill. Omitted on the client portal. */
  pill?: ReactNode;
  /** The overline row's text — "Seller representation . Getting ready" for a client, "Your closing checklist." for an attorney. */
  overline: ReactNode;
  /** A secondary line under the overline — "Client: Mohammad Riyan · Pre-listing" for externals. Omitted on the client portal. */
  meta?: ReactNode;
  testId?: string;
}

export default function PortalAddressBlock({ address, pill, overline, meta, testId = "portal-address" }: PortalAddressBlockProps) {
  return (
    <div className="min-w-0 animate-in fade-in slide-in-from-bottom-4 duration-500 ease-out">
      <h1
        className="break-words font-display text-[38px] font-semibold leading-[0.98] tracking-normal text-dash-ink sm:text-[52px] lg:text-[60px]"
        data-testid={testId}
      >
        {address}
      </h1>
      <div className="mt-3 flex flex-wrap items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.28em] text-dash-muted">
        {pill}
        <span>{overline}</span>
      </div>
      {meta && (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[13px] normal-case tracking-normal text-dash-muted">
          {meta}
        </div>
      )}
    </div>
  );
}
